import { localDateTimeToUtc, localDateToUtcStart, localDateToUtcEnd } from '../utils/timezone.ts';

export type DoctorMode = 'pecking-order' | 'shift' | '';

export interface ShiftPayload {
  employeeId: string;
  startTimeUtc: string;
  endTimeUtc: string;
  employeeType: string;
  order?: number;
}

export interface BuildShiftPayloadsInput {
  employeeType: string;
  doctorMode: DoctorMode;
  shiftDate: string;
  startTime: string;
  endTime: string;
  selectedEmployeeIds: string[];
  orderMap: Record<string, number>;
}

export type BuildShiftPayloadsResult =
  | { ok: true; shifts: ShiftPayload[] }
  | { ok: false; error: string };

export const buildShiftPayloads = (input: BuildShiftPayloadsInput): BuildShiftPayloadsResult => {
  const { employeeType, doctorMode, shiftDate, startTime, endTime, selectedEmployeeIds, orderMap } = input;

  if (!employeeType) {
    return { ok: false, error: 'Please select an employee type' };
  }
  if (selectedEmployeeIds.length === 0) {
    return { ok: false, error: 'Please select at least one employee' };
  }

  const isDoctor = employeeType === 'doctor';
  const isPeckingOrder = isDoctor && doctorMode === 'pecking-order';

  if (isDoctor && doctorMode !== 'pecking-order' && doctorMode !== 'shift') {
    return { ok: false, error: 'Please choose pecking order or shift doctor' };
  }

  if (isPeckingOrder) {
    if (!shiftDate) {
      return { ok: false, error: 'Shift date is required' };
    }
    const missingOrder = selectedEmployeeIds.find(id => orderMap[id] === undefined);
    if (missingOrder) {
      return { ok: false, error: 'Every selected doctor must have a pecking order value' };
    }

    const startTimeUtc = localDateToUtcStart(shiftDate);
    const endTimeUtc = localDateToUtcEnd(shiftDate);

    const shifts: ShiftPayload[] = selectedEmployeeIds.map(employeeId => ({
      employeeId,
      startTimeUtc,
      endTimeUtc,
      employeeType,
      order: orderMap[employeeId],
    }));

    return { ok: true, shifts };
  }

  if (!startTime || !endTime) {
    return { ok: false, error: 'Start and end times are required' };
  }

  const startTimeUtc = localDateTimeToUtc(startTime);
  const endTimeUtc = localDateTimeToUtc(endTime);

  const shifts: ShiftPayload[] = selectedEmployeeIds.map(employeeId => ({
    employeeId,
    startTimeUtc,
    endTimeUtc,
    employeeType,
  }));

  return { ok: true, shifts };
};

export interface FilterableEmployee {
  employeeId: string;
  firstName: string;
  lastName: string;
  employeeType: string;
  homeHospitalId?: string;
}

export const filterEmployeesByTypeAndHospital = <T extends FilterableEmployee>(
  employees: T[],
  employeeType: string,
  homeHospitalFilter: string,
): T[] =>
  employees
    .filter(e => e.employeeType === employeeType)
    .filter(e => !homeHospitalFilter || e.homeHospitalId === homeHospitalFilter)
    .sort((a, b) =>
      `${a.lastName} ${a.firstName}`.localeCompare(`${b.lastName} ${b.firstName}`),
    );
